<template>
  <div class="dropdown">
    <button
      class="ui-icon-btn ui-lang-toggle"
      type="button"
      data-bs-toggle="dropdown"
      data-bs-display="static"
      aria-expanded="false"
      :aria-label="$t('header.language')"
      :title="$t('header.language')"
    >
      <span class="ui-lang-code" :lang="current.code">{{ current.short }}</span>
    </button>
    <div class="dropdown-menu dropdown-menu-end ui-lang-menu">
      <div class="ui-lang-head">{{ $t("header.language") }}</div>
      <button
        v-for="l in locales"
        :key="l.code"
        type="button"
        class="dropdown-item ui-lang-item"
        :class="{ active: l.code === current.code }"
        :aria-pressed="l.code === current.code"
        @click="choose(l.code)"
      >
        <span class="ui-lang-item-code" :lang="l.code">{{ l.short }}</span>
        <span class="ui-lang-item-label" :lang="l.code">{{ l.label }}</span>
        <i v-if="l.code === current.code" class="ph ph-check ui-lang-item-check" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { useI18n } from "vue-i18n";
import { LOCALES, localeMeta, setLocale, type Locale } from "@/i18n";

export default defineComponent({
  name: "LanguageSwitcher",
  setup() {
    const { locale } = useI18n();
    const current = computed(() => localeMeta(locale.value as Locale));

    function choose(code: Locale) {
      setLocale(code);
    }

    return { locales: LOCALES, current, choose };
  },
});
</script>

<style scoped>
.dropdown {
  position: relative;
}
.ui-lang-code {
  font-size: 12px;
  font-weight: var(--ui-fw-black);
  letter-spacing: 0.02em;
}
.ui-lang-code:lang(ar) {
  font-family: var(--ui-font-arabic);
  font-size: 17px;
}
.ui-lang-toggle[aria-expanded="true"] {
  color: #FFFFFF;
  background: var(--ui-accent);
  border-color: var(--ui-accent-strong);
}

/* Same placement as the other header menus: under the toggle, end-aligned */
.ui-lang-menu {
  position: absolute;
  top: 100% !important;
  right: 0 !important;
  left: auto !important;
  transform: none !important;
  min-width: 200px;
  margin-top: 10px !important;
}
.ui-lang-head {
  padding: 8px 12px 10px;
  margin-bottom: 6px;
  border-bottom: 1px solid var(--ui-border);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.ui-lang-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ui-lang-item-code {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--ui-surface-sunken);
  color: var(--ui-accent-text);
  font-size: 11px;
  font-weight: var(--ui-fw-black);
}
.ui-lang-item-label:lang(ar),
.ui-lang-item-code:lang(ar) {
  font-family: var(--ui-font-arabic);
}
.ui-lang-item-code:lang(ar) {
  font-size: 15px;
}
.ui-lang-item-label {
  flex: 1 1 auto;
  font-weight: var(--ui-fw-semibold);
}
.ui-lang-item.active,
.ui-lang-item:active {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
.ui-lang-item.active .ui-lang-item-code {
  background: var(--ui-accent);
  color: #FFFFFF;
}
.ui-lang-item-check {
  color: var(--ui-accent-text);
  font-size: 16px;
}

@media (max-width: 767.98px) {
  /* Phones: full-width panel just below the header, like the other menus */
  .ui-lang-menu {
    position: fixed !important;
    top: calc(var(--ui-frame-gap) + 60px) !important;
    left: var(--ui-frame-gap) !important;
    right: var(--ui-frame-gap) !important;
    width: auto !important;
    min-width: 0;
  }
}
</style>
